import { type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { SITE_ORIGIN, publicHref } from '../lib/seo';
import { PublicFooter } from './PublicFooter';
import { PublicHeader } from './PublicHeader';
import { cn } from './ui';
import { breadcrumbSchema, WEBSITE_ID } from '../lib/seo';
import { useLegalPagesVisible } from '../lib/useLegalPagesVisible';
import type { LegalPageKey } from '../lib/types';

const LEGAL_NAV: Array<{ to: string; end: boolean; label: string; page?: LegalPageKey }> = [
  { to: '/legal', end: true, label: 'Overview' },
  { to: '/privacy', end: false, label: 'Privacy Policy', page: 'privacy' },
  { to: '/terms', end: false, label: 'Terms of Use', page: 'terms' },
  { to: '/dpa', end: false, label: 'DPA', page: 'dpa' },
  { to: '/gdpr', end: false, label: 'GDPR compliance', page: 'gdpr' },
  { to: '/aup', end: false, label: 'Acceptable Use', page: 'aup' },
  { to: '/subprocessors', end: false, label: 'Sub-processors', page: 'subprocessors' },
  { to: '/cookies', end: false, label: 'Cookie Policy', page: 'cookies' },
  { to: '/contact', end: false, label: 'Contact', page: 'contact' },
];

/**
 * Two-column layout for /privacy, /terms, /dpa, /gdpr, /aup, /subprocessors, /contact.
 * Sticky sidebar nav on the left, scrollable content on the right. Reuses PublicFooter
 * so the same legal links + business identity appear at the bottom of every page.
 */
export function LegalLayout({
  title,
  lastUpdated,
  children,
}: {
  title: string;
  lastUpdated?: string;
  children: ReactNode;
}) {
  const { isVisible } = useLegalPagesVisible();
  const nav = LEGAL_NAV.filter((item) => item.page == null || isVisible(item.page));
  // BreadcrumbList (Home › Legal › <this page>) for every legal page, centralised here so all
  // 8 content pages get it without per-page edits. Adds ONLY a JSON-LD <script> to <head> —
  // the page's own <Seo> still owns title/description/canonical (Helmet merges them).
  const { pathname } = useLocation();
  const origin = SITE_ORIGIN;
  const canonicalPath = pathname.endsWith('/') ? pathname : `${pathname}/`;
  const breadcrumb = breadcrumbSchema([
    { name: 'Home', url: `${origin}/` },
    { name: 'Legal', url: `${origin}/legal/` },
    { name: title, url: `${origin}${canonicalPath}` },
  ]);
  // A WebPage node so the human-visible "Last updated" line also exists as a machine-readable
  // freshness signal — legal/policy pages are exactly where an LLM wants to know how current the
  // terms are. `dateModified` must be ISO 8601, so only emit it when the display string parses to
  // a real date (an unparseable one is worse than none — it asserts a bogus date).
  const modified = lastUpdated ? new Date(lastUpdated) : null;
  const webpage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    url: `${origin}${canonicalPath}`,
    isPartOf: { '@id': WEBSITE_ID },
    ...(modified && !Number.isNaN(modified.getTime())
      ? { dateModified: modified.toISOString().slice(0, 10) }
      : {}),
  };
  return (
    <>
      {/* Rendered in place: React 19 hoists title/meta/link into <head> but NOT scripts, and
          JSON-LD does not need to be there — both Google and Bing read ld+json from the body. */}
      <script type="application/ld+json">{JSON.stringify([webpage, breadcrumb])}</script>
      <div className="mx-auto max-w-[1210px] px-6 pb-10">
        <PublicHeader />

      {/* mt-14 matches the top breathing room every other public page gives its
          first section (Docs / Pricing / Features / Landing). Without it the
          content butts right up under the sticky header on large viewports. */}
      <div className="mt-14 grid gap-10 md:grid-cols-[220px_1fr]">
        <nav className="hidden md:block">
          <div className="sticky top-6 flex flex-col gap-1.5">
            <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.16em] text-faint">
              Legal
            </div>
            {nav.map(({ to, end, label }) => (
              <NavLink
                key={to}
                // Trailing-slash href (nginx 301s the bare form). `key` stays bare — and so does
                // NavLink's own isActive matching, which normalises trailing slashes itself.
                to={publicHref(to)}
                end={end}
                className={({ isActive }) =>
                  cn(
                    'rounded-md px-2 py-1.5 text-[13.5px] transition',
                    isActive
                      ? 'bg-panel2 text-txt'
                      : 'text-muted hover:bg-hover hover:text-txt',
                  )
                }
              >
                {label}
              </NavLink>
            ))}
          </div>
        </nav>

        <article className="min-w-0">
          <h1 className="font-display text-4xl font-extrabold tracking-tight">{title}</h1>
          {lastUpdated && (
            <p className="mt-2 font-mono text-[11.5px] text-faint">Last updated: {lastUpdated}</p>
          )}

          <div className="prose mt-8 max-w-none text-[14.5px] leading-relaxed text-muted [&_a]:text-accent [&_code]:rounded [&_code]:bg-panel2 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[12.5px] [&_code]:text-txt [&_h2]:mt-10 [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-txt [&_h3]:mt-6 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-txt [&_li]:mt-1 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:my-3 [&_strong]:text-txt [&_table]:my-4 [&_table]:w-full [&_td]:border-b [&_td]:border-bordersoft [&_td]:px-2 [&_td]:py-2 [&_th]:border-b [&_th]:border-bordersoft [&_th]:px-2 [&_th]:py-2 [&_th]:text-left [&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-6">
            {children}
          </div>
        </article>
      </div>

        <PublicFooter />
      </div>
    </>
  );
}
